import { ref, computed, watch } from 'vue';
import store from '@/store';
import { config } from '@/config.js';

/**
 * useResumenVentasDetalle — drill-down de la tabla de ventas: sesiones de un
 * evento y desglose por zona/precio de una sesión.
 *
 * El estado va POR NIVEL Y POR ID (no un `loading` global como en el legacy):
 * con un único booleano, cargar el desglose de una sesión ponía el spinner de
 * todas las filas abiertas. Aquí cada evento y cada sesión tienen su propio
 * `loading` y su propio `error`, y cada uno se pide UNA vez (carga perezosa:
 * al abrir, no al listar).
 *
 * Payloads, con los MISMOS nombres de filtro que el listado:
 *  - sesiones: `{ filters: { fecha_inicio, fecha_fin, taquillero_id }, event_id }`
 *  - desglose: `{ filters: { taquillero_id, fecha_inicio, fecha_fin },
 *                 event_id, sesion_id }`
 *
 * El backend lee `fecha_inicio`/`fecha_fin`/`taquillero_id`. El legacy de
 * sesiones mandaba `fecha_desde`/`fecha_hasta` (que la API ignoraba) y nunca
 * el taquillero: el panel de sesiones pedía el universo sin filtrar mientras
 * la fila del evento mostraba un recorte, así que sus totales no cuadraban.
 *
 * GOTCHA Vue 2.7: añadir claves nuevas a un `ref({})` no es reactivo. Todas las
 * escrituras reemplazan el objeto entero (`{ ...previo, [clave]: valor }`).
 *
 * @param {{
 *   filtros?: Object|Function,
 *   fetcherSesiones?: Function,
 *   fetcherDesglose?: Function
 * }} [opciones]
 *   filtros — objeto (o getter) con los filtros vigentes del listado. Cuando
 *   cambian, el detalle se vacía: las sesiones y los desgloses cargados
 *   pertenecen a los filtros anteriores.
 * @returns {Object} estado y acciones del drill-down.
 */
export function useResumenVentasDetalle({ filtros, fetcherSesiones, fetcherDesglose } = {}) {
    /**
     * Estado por evento: `{ [eventoId]: { abierto, sesiones, sesionError,
     * cargandoSesiones, sesionesCargadas } }`.
     */
    const eventos = ref({});
    /**
     * Estado por sesión, con clave compuesta `'<eventoId>:<sesionId>'`:
     * `{ abierto, desglose, desgloseError, cargandoDesglose, desgloseCargado }`.
     */
    const sesiones = ref({});

    /**
     * Token de la última petición lanzada por clave: la última gana. Sin esto,
     * una respuesta lenta de una sesión cerrada podía escribir el desglose de
     * otra.
     *
     * El token sale de un contador GLOBAL Y MONOTÓNICO que NUNCA se reinicia:
     * `resetDetalle` borra las CLAVES, no el contador. Con un contador por clave
     * que se reiniciase al limpiar el estado, la petición lanzada justo después
     * del reset reutilizaba el mismo número que la anterior —que seguía en
     * vuelo— y al responder la antigua el guard `peticion === peticiones[clave]`
     * daba `true`: se pintaban sesiones/ventas calculadas con el rango anterior
     * y quedaban marcadas como cargadas.
     *
     * Borrar las claves en `resetDetalle` sigue siendo necesario: es lo que
     * invalida las peticiones que están en vuelo en el momento del reset.
     */
    const peticiones = {};
    let generacion = 0;

    /**
     * Reserva el siguiente token para una clave. El token es único durante toda
     * la vida del composable, así que una petición vieja jamás puede volver a
     * coincidir con el de la clave que ocupa su lugar.
     *
     * @param {string} clave
     * @returns {number} token con el que comprobar la vigencia al resolver.
     */
    function nuevaPeticion(clave) {
        generacion += 1;
        peticiones[clave] = generacion;

        return generacion;
    }

    /**
     * @param {string} clave
     * @param {number} token Devuelto por `nuevaPeticion`.
     * @returns {boolean} `true` si sigue siendo la última petición de esa clave.
     */
    function esVigente(clave, token) {
        return peticiones[clave] === token;
    }

    const leerFiltros = () => (typeof filtros === 'function' ? (filtros() || {}) : (filtros || {}));

    const claveSesion = (eventoId, sesionId) => `${eventoId}:${sesionId}`;

    /** Reemplaza el estado de un evento (escritura reactiva segura en Vue 2.7). */
    function patchEvento(eventoId, cambios) {
        const previo = eventos.value[eventoId] || {};
        eventos.value = { ...eventos.value, [eventoId]: { ...previo, ...cambios } };
    }

    /** Reemplaza el estado de una sesión. */
    function patchSesion(clave, cambios) {
        const previo = sesiones.value[clave] || {};
        sesiones.value = { ...sesiones.value, [clave]: { ...previo, ...cambios } };
    }

    async function dispatchSesiones({ event_id, filters }) {
        return store.dispatch(config.getVuexAlias('back.events.resumen-ventas.sesiones'), { event_id, filters });
    }

    async function dispatchDesglose({ event_id, sesion_id, filters }) {
        return store.dispatch(config.getVuexAlias('back.events.resumen-ventas.sesion.desglose'), { event_id, sesion_id, filters });
    }

    const doSesiones = fetcherSesiones || dispatchSesiones;
    const doDesglose = fetcherDesglose || dispatchDesglose;

    /** Lista dentro del body paginado, con red de seguridad. */
    const comoLista = res => {
        const payload = (res && res.data) || {};

        return Array.isArray(payload.data) ? payload.data : [];
    };

    /**
     * Pide las sesiones de un evento. Sólo la usa el toggle: si ya están
     * cargadas, no vuelve a pedirlas.
     *
     * @param {number|string} eventoId
     */
    async function cargarSesiones(eventoId) {
        const clave = String(eventoId);
        const peticion = nuevaPeticion(clave);

        patchEvento(eventoId, { cargandoSesiones: true, sesionError: null });

        try {
            const filtrosActuales = leerFiltros();
            const res = await doSesiones({
                event_id: eventoId,
                filters: {
                    fecha_inicio: filtrosActuales.fecha_inicio ?? null,
                    fecha_fin: filtrosActuales.fecha_fin ?? null,
                    taquillero_id: filtrosActuales.taquillero_id ?? ''
                }
            });

            if (!esVigente(clave, peticion)) return;

            patchEvento(eventoId, {
                sesiones: comoLista(res),
                sesionesCargadas: true,
                cargandoSesiones: false
            });
        } catch (e) {
            if (!esVigente(clave, peticion)) return;

            // El error se expone por evento: el legacy guardaba un solo
            // `sesionError` global y el mensaje aparecía en todas las filas.
            patchEvento(eventoId, { sesionError: e, cargandoSesiones: false, sesiones: [] });
            if (process.env.NODE_ENV !== 'production') console.error(e);
        }
    }

    /**
     * Pide el desglose por zona/precio de una sesión.
     *
     * @param {number|string} eventoId
     * @param {Object} sesion Sesión con `id`.
     */
    async function cargarDesglose(eventoId, sesion) {
        const clave = claveSesion(eventoId, sesion.id);
        const peticion = nuevaPeticion(clave);

        patchSesion(clave, { cargandoDesglose: true, desgloseError: null });

        try {
            const filtrosActuales = leerFiltros();
            const res = await doDesglose({
                event_id: eventoId,
                sesion_id: sesion.id,
                filters: {
                    taquillero_id: filtrosActuales.taquillero_id ?? '',
                    fecha_inicio: filtrosActuales.fecha_inicio ?? null,
                    fecha_fin: filtrosActuales.fecha_fin ?? null
                }
            });

            if (!esVigente(clave, peticion)) return;

            patchSesion(clave, {
                desglose: comoLista(res),
                desgloseCargado: true,
                cargandoDesglose: false
            });
        } catch (e) {
            if (!esVigente(clave, peticion)) return;

            patchSesion(clave, { desgloseError: e, cargandoDesglose: false, desglose: [] });
            if (process.env.NODE_ENV !== 'production') console.error(e);
        }
    }

    /**
     * Abre o cierra el desplegable de sesiones de un evento. Al abrir la
     * primera vez se piden las sesiones.
     *
     * @param {Object} evento
     */
    function alternarSesiones(evento) {
        const estado = eventos.value[evento.id] || {};
        const abierto = !estado.abierto;

        patchEvento(evento.id, { abierto });

        if (abierto && !estado.sesionesCargadas) cargarSesiones(evento.id);
    }

    /**
     * Abre o cierra el desglose de ventas de una sesión. Al abrir la primera
     * vez se pide.
     *
     * @param {number|string} eventoId
     * @param {Object} sesion
     */
    function alternarDesglose(eventoId, sesion) {
        const clave = claveSesion(eventoId, sesion.id);
        const estado = sesiones.value[clave] || {};
        const abierto = !estado.abierto;

        patchSesion(clave, { abierto });

        if (abierto && !estado.desgloseCargado) cargarDesglose(eventoId, sesion);
    }

    /** Fuerza la recarga de una clave ignorando la caché (acción "Reintentar"). */
    function reintentarSesiones(eventoId) {
        patchEvento(eventoId, { sesionesCargadas: false });
        return cargarSesiones(eventoId);
    }

    /**
     * @param {number|string} eventoId
     * @param {Object} sesion
     */
    function reintentarDesglose(eventoId, sesion) {
        patchSesion(claveSesion(eventoId, sesion.id), { desgloseCargado: false });
        return cargarDesglose(eventoId, sesion);
    }

    const estadoEvento = eventoId => eventos.value[String(eventoId)] || {};

    /** `true` si el evento tiene su desplegable de sesiones abierto. */
    const sesionesAbiertas = eventoId => !!estadoEvento(eventoId).abierto;

    /**
     * @param {number|string} eventoId
     * @param {Object} sesion
     * @returns {boolean} `true` si la sesión muestra su desglose.
     */
    const desgloseAbierto = (eventoId, sesion) => !!(sesiones.value[claveSesion(eventoId, sesion.id)] || {}).abierto;

    /** Sesiones ya cargadas de un evento (array vacío si todavía no hay). */
    const sesionesDe = eventoId => estadoEvento(eventoId).sesiones || [];

    /**
     * Estado de carga del desglose de una sesión.
     *
     * @param {number|string} eventoId
     * @param {Object} sesion
     */
    const estadoDesglose = (eventoId, sesion) => sesiones.value[claveSesion(eventoId, sesion.id)] || {};

    /**
     * Vacía el drill-down: se usa al cambiar de filtros.
     *
     * Borra los tokens en vuelo (de ahí que las respuestas que aún no han
     * llegado queden descartadas) pero NO el contador `generacion`: los tokens
     * no se reutilizan nunca.
     */
    function resetDetalle() {
        eventos.value = {};
        sesiones.value = {};
        Object.keys(peticiones).forEach(clave => delete peticiones[clave]);
    }

    /**
     * Firma de los filtros que afectan al detalle (todo menos la paginación:
     * cambiar de página cambia las filas, pero no invalida un desglose abierto
     * del evento que sigue en pantalla... salvo que el evento ya no esté, y eso
     * lo resuelve el propio v-for).
     */
    const firmaFiltros = computed(() => {
        const f = leerFiltros();

        return [
            f.search, f.search_teatro_id, f.taquillero_id, f.teatro_company_id,
            f.fecha_inicio, f.fecha_fin, f.pasados, f.actuales
        ].join('|');
    });

    watch(firmaFiltros, resetDetalle);

    return {
        eventos,
        sesiones,
        cargarSesiones,
        cargarDesglose,
        alternarSesiones,
        alternarDesglose,
        reintentarSesiones,
        reintentarDesglose,
        resetDetalle,
        sesionesAbiertas,
        desgloseAbierto,
        sesionesDe,
        estadoEvento,
        estadoDesglose
    };
}
